import React from "react";
import { CircularProgress } from "@mui/material";
import s from "./styles.scss";
import withStyles from "isomorphic-style-loader/withStyles";

interface PrimaryButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  variant?: "primary" | "secondary" | "darkBlue";
  loading?: boolean;
  loadingText?: string;
  loadingAnimation?: "pulse" | "shimmer" | "none";
  type?: "button" | "submit" | "reset";
}

const PrimaryButton: React.FC<PrimaryButtonProps> = ({
  children,
  onClick,
  className = "",
  disabled = false,
  variant = "primary",
  loading = false,
  loadingText = "Loading...",
  loadingAnimation = "pulse",
  type = "button",
}) => {
  const buttonClass =
    variant === "secondary"
      ? s.buttonSecondary
      : variant === "darkBlue"
      ? s.buttonDarkBlue
      : s.buttonBlue;
  const loadingClass = loading
    ? loadingAnimation === "pulse"
      ? s.loadingPulse
      : loadingAnimation === "shimmer"
      ? s.loadingShimmer
      : s.loading
    : "";

  const renderContent = () => {
    if (loading) {
      if (loadingAnimation === "shimmer") {
        return (
          <div className={s.loadingContent}>
            <div className={s.spinnerWrapper}>
              <CircularProgress
                size={20}
                color="inherit"
                className={s.spinner}
                thickness={3}
              />
              <div className={s.pulseRing}></div>
            </div>
            <span className={s.loadingTextShimmer}>
              {loadingText.split("").map((char, index) => (
                <span
                  key={index}
                  className={s.loadingDot}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {char === " " ? "\u00A0" : char}
                </span>
              ))}
            </span>
          </div>
        );
      }
      return (
        <div className={s.loadingContent}>
          <div className={s.spinnerWrapper}>
            <CircularProgress
              size={22}
              color="inherit"
              className={s.spinner}
              thickness={3}
            />
          </div>
          <span className={s.loadingText}>
            {loadingText}
            <span className={s.loadingDots}>
              <span className={s.dot} style={{ animationDelay: "0s" }}>
                .
              </span>
              <span className={s.dot} style={{ animationDelay: "0.2s" }}>
                .
              </span>
              <span className={s.dot} style={{ animationDelay: "0.4s" }}>
                .
              </span>
            </span>
          </span>
        </div>
      );
    }
    return <span className={s.buttonText}>{children}</span>;
  };

  return (
    <button
      type={type}
      onClick={loading ? undefined : onClick}
      className={`${buttonClass} ${className} ${loadingClass}`}
      disabled={disabled || loading}
    >
      {renderContent()}
    </button>
  );
};

export default withStyles(s)(PrimaryButton);
